import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import api, { BACKEND, getToken } from "@/lib/api";
import {
  Check, Plus, Trash2, ArrowUp, ArrowDown, Upload, FileText, CalendarDays, Info, X, ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

function OptionCard({ selected, label, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={cn(
        "group relative flex items-center gap-3 rounded-xl border p-4 text-left transition-colors duration-200",
        selected
          ? "border-sky-500 bg-sky-50 ring-1 ring-sky-500"
          : "border-slate-200 bg-white hover:border-sky-300 hover:bg-sky-50/40"
      )}
    >
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors",
          selected ? "border-sky-500 bg-sky-500 text-white" : "border-slate-300 bg-white"
        )}
      >
        {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      </span>
      <span className={cn("text-sm font-semibold", selected ? "text-sky-900" : "text-slate-700")}>{label}</span>
    </button>
  );
}

export function QuestionRenderer({ question, value, onChange, projectId }) {
  const q = question;
  const t = q.type;

  if (t === "yes_no") {
    return (
      <div className="grid max-w-lg grid-cols-2 gap-4">
        {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map((o) => (
          <button
            key={o.l}
            type="button"
            data-testid={`answer-${q.key}-${o.l.toLowerCase()}`}
            onClick={() => onChange(o.v)}
            className={cn(
              "rounded-xl border p-6 text-center font-heading text-lg font-bold transition-colors duration-200",
              value === o.v
                ? "border-sky-500 bg-sky-50 text-sky-700 ring-1 ring-sky-500"
                : "border-slate-200 bg-white text-slate-600 hover:border-sky-300 hover:bg-sky-50/40"
            )}
          >
            {o.l}
          </button>
        ))}
      </div>
    );
  }

  if (t === "single_select") {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {q.options.map((o) => (
          <OptionCard key={o.value} label={o.label} selected={value === o.value}
            testid={`answer-${q.key}-${o.value}`}
            onClick={() => onChange(o.value)} />
        ))}
      </div>
    );
  }

  if (t === "multi_select") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {q.options.map((o) => (
          <OptionCard key={o.value} label={o.label} selected={arr.includes(o.value)}
            testid={`answer-${q.key}-${o.value}`}
            onClick={() => toggle(o.value)} />
        ))}
      </div>
    );
  }

  if (t === "dropdown") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`answer-${q.key}`} className="max-w-md h-12 rounded-lg">
          <SelectValue placeholder="Select an option…" />
        </SelectTrigger>
        <SelectContent>
          {q.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }

  if (t === "short_text") {
    return <Input data-testid={`answer-${q.key}`} className="max-w-md h-12 rounded-lg" value={value || ""}
      placeholder={q.example || "Type your answer…"} onChange={(e) => onChange(e.target.value)} />;
  }

  if (t === "long_text") {
    return <Textarea data-testid={`answer-${q.key}`} className="min-h-[140px] max-w-2xl rounded-lg" value={value || ""}
      placeholder={q.example || "Type your answer…"} onChange={(e) => onChange(e.target.value)} />;
  }

  if (t === "number") {
    return <Input type="number" data-testid={`answer-${q.key}`} className="max-w-[200px] h-12 rounded-lg" value={value ?? ""}
      placeholder="0" onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />;
  }

  if (t === "currency") {
    return (
      <div className="relative max-w-[240px]">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-semibold text-slate-400">₹</span>
        <Input type="number" data-testid={`answer-${q.key}`} className="h-12 rounded-lg pl-8" value={value ?? ""}
          placeholder="0.00" onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
      </div>
    );
  }

  if (t === "date") {
    const dateVal = value ? new Date(value) : undefined;
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid={`answer-${q.key}`} className="h-12 w-[260px] justify-start rounded-lg font-normal">
            <CalendarDays className="mr-2 h-4 w-4 text-slate-400" />
            {dateVal ? format(dateVal, "PPP") : <span className="text-slate-400">Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={dateVal} onSelect={(d) => onChange(d ? d.toISOString() : "")} />
        </PopoverContent>
      </Popover>
    );
  }

  if (t === "process_steps") return <ProcessSteps value={value} onChange={onChange} qkey={q.key} />;
  if (t === "table") return <RepeatingTable value={value} onChange={onChange} columns={q.options || []} qkey={q.key} />;
  if (t === "hierarchy") return <HierarchyBuilder value={value} onChange={onChange} qkey={q.key} />;
  if (t === "file_upload") return <FileUploadField projectId={projectId} module={q.module} qkey={q.key} value={value} onChange={onChange} />;
  if (t === "tags") return <TagInput value={value} onChange={onChange} qkey={q.key} placeholder={q.example} />;

  if (t === "info") {
    return (
      <div className="flex max-w-2xl items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 p-5">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-sky-500" />
        <p className="text-sm text-slate-700">{q.example || q.help_text}</p>
      </div>
    );
  }

  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

function ProcessSteps({ value, onChange, qkey }) {
  const steps = Array.isArray(value) ? value : [];
  const update = (next) => onChange(next);
  const add = () => update([...steps, { text: "", notes: "" }]);
  const remove = (i) => update(steps.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
  };
  const setField = (i, field, val) => {
    const next = [...steps];
    next[i] = { ...next[i], [field]: val };
    update(next);
  };
  return (
    <div className="max-w-2xl space-y-3">
      {steps.map((s, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sm font-bold text-sky-700">{i + 1}</div>
            <div className="flex-1 space-y-2">
              <Input data-testid={`step-${qkey}-${i}`} value={s.text} placeholder="Describe this step…"
                onChange={(e) => setField(i, "text", e.target.value)} className="h-10 rounded-lg" />
              <Input value={s.notes || ""} placeholder="Notes (optional)"
                onChange={(e) => setField(i, "notes", e.target.value)} className="h-9 rounded-lg text-sm" />
            </div>
            <div className="flex flex-col gap-1">
              <button type="button" onClick={() => move(i, -1)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><ArrowDown className="h-4 w-4" /></button>
              <button type="button" onClick={() => remove(i)} className="rounded p-1 text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} data-testid={`add-step-${qkey}`} className="rounded-lg">
        <Plus className="mr-2 h-4 w-4" /> Add Step
      </Button>
    </div>
  );
}

function AutoGrowCell({ value, onChange, placeholder, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = Math.max(38, el.scrollHeight) + "px"; }
  }, [value]);
  return (
    <textarea
      ref={ref}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={1}
      className="w-full min-h-[38px] resize-none overflow-hidden rounded-md border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-slate-800 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300"
    />
  );
}

function RepeatingTable({ value, onChange, columns, qkey }) {
  const rows = Array.isArray(value) ? value : [];
  const add = () => onChange([...rows, Object.fromEntries(columns.map((c) => [c.value, ""]))]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const setCell = (i, key, val) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  return (
    <div className="max-w-4xl space-y-3">
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => <th key={c.value} className="px-3 py-2.5 text-left align-top font-semibold text-slate-600">{c.label}</th>)}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-3 py-6 text-center text-slate-400">No rows yet. Add your first row below.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                {columns.map((c) => (
                  <td key={c.value} className="px-2 py-2 align-top">
                    <AutoGrowCell testid={`cell-${qkey}-${i}-${c.value}`} value={r[c.value] || ""} onChange={(val) => setCell(i, c.value, val)} placeholder={c.label} />
                  </td>
                ))}
                <td className="px-2 text-center">
                  <button type="button" onClick={() => remove(i)} className="rounded p-1 text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button type="button" variant="outline" onClick={add} data-testid={`add-row-${qkey}`} className="rounded-lg">
        <Plus className="mr-2 h-4 w-4" /> Add Row
      </Button>
    </div>
  );
}

function HierarchyBuilder({ value, onChange, qkey }) {
  const levels = Array.isArray(value) ? value : [];
  const update = (n) => onChange(n);
  const add = () => update([...levels, ""]);
  const setL = (i, v) => { const n = [...levels]; n[i] = v; update(n); };
  const remove = (i) => update(levels.filter((_, x) => x !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= levels.length) return;
    const n = [...levels];
    [n[i], n[j]] = [n[j], n[i]];
    update(n);
  };
  const filled = levels.filter(Boolean);
  return (
    <div className="max-w-xl space-y-2">
      {levels.map((l, i) => (
        <div key={i}>
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">{i + 1}</span>
            <Input data-testid={`hier-${qkey}-${i}`} value={l} placeholder={i === 0 ? "Top of the hierarchy (e.g. Sales Head)" : "Next level down…"}
              onChange={(e) => setL(i, e.target.value)} className="h-10 rounded-lg" />
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="rounded p-1 text-slate-400 hover:bg-slate-100"><ArrowDown className="h-4 w-4" /></button>
              <button type="button" onClick={() => remove(i)} className="rounded p-1 text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          {i < levels.length - 1 && <div className="ml-3.5 h-3 border-l-2 border-slate-200" />}
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} data-testid={`add-level-${qkey}`} className="rounded-lg">
        <Plus className="mr-2 h-4 w-4" /> Add level
      </Button>
      {filled.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-lg bg-slate-50 p-3 text-xs">
          {filled.map((l, i) => (
            <span key={i} className="flex items-center gap-1.5">
              <span className="rounded bg-white px-2 py-1 font-semibold text-slate-700 ring-1 ring-slate-200">{l}</span>
              {i < filled.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-300" />}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FileUploadField({ projectId, module, qkey, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("module", module || "");
      const { data } = await api.post(`/projects/${projectId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ id: data.id, name: data.original_filename });
      toast.success("File uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="max-w-md">
      {value?.name ? (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <FileText className="h-5 w-5 text-emerald-600" />
          <span className="flex-1 truncate text-sm font-semibold text-emerald-800">{value.name}</span>
          <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
        </div>
      ) : (
        <label data-testid={`upload-${qkey}`} className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center transition-colors hover:border-sky-400 hover:bg-sky-50/40">
          <Upload className="h-6 w-6 text-slate-400" />
          <span className="text-sm font-semibold text-slate-600">{uploading ? "Uploading…" : "Click to upload a document"}</span>
          <span className="text-xs text-slate-400">Excel, PDF, images or screenshots</span>
          <input type="file" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

function TagInput({ value, onChange, qkey, placeholder }) {
  const [text, setText] = useState("");
  const tags = Array.isArray(value) ? value : [];
  const add = () => {
    const t = text.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setText("");
  };
  const remove = (t) => onChange(tags.filter((x) => x !== t));
  return (
    <div className="max-w-md">
      {tags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {tags.map((t) => (
            <span key={t} data-testid={`tag-${qkey}-${t}`} className="inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-sm font-semibold text-sky-700">
              {t}
              <button type="button" onClick={() => remove(t)} className="text-sky-400 hover:text-sky-700"><X className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input data-testid={`answer-${qkey}`} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder || "Type and press Enter…"} className="h-11 rounded-lg" />
        <Button type="button" variant="outline" data-testid={`add-tag-${qkey}`} onClick={add} className="rounded-lg"><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
